<template>
  <aside class="document-comments-panel">
    <header class="document-comments-header">
      <h2 class="document-comments-title">Comments</h2>
      <button type="button" class="icon-button" title="Close comments" @click="$emit('close')">
        <X class="icon" />
      </button>
    </header>
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <div class="document-comments-body">
      <div v-if="comments == null" class="document-comments-empty">Loading comments…</div>
      <div v-else-if="comments.comments.length === 0" class="document-comments-empty">No comments yet.</div>
      <article
        v-for="comment in comments?.comments ?? []"
        :key="comment.id"
        class="comment-item"
        :class="{ 'comment-item-own': isCommentOwner(comment) }"
      >
        <UserAvatar :name="comment.userName" size="medium" class="comment-item-avatar" aria-hidden="true" />
        <div class="comment-bubble">
          <header class="comment-item-header">
            <strong>{{ comment.userName }}</strong>
          </header>
          <p v-if="editingCommentId !== comment.id" class="comment-item-content">{{ comment.content }}</p>
          <textarea
            v-else
            v-model="commentEdits[comment.id]"
            class="comment-item-editor"
            maxlength="4000"
          ></textarea>
          <div v-if="editingCommentId === comment.id" class="comment-item-actions">
            <button type="button" class="btn" @click="saveEdit(comment)">Save</button>
            <button type="button" class="btn btn-ghost" @click="cancelEdit(comment)">Cancel</button>
          </div>
          <div v-else-if="canShowChanges(comment) || canEditComment(comment) || canDeleteComment(comment)" class="comment-item-actions">
            <button
              v-if="canShowChanges(comment)"
              type="button"
              class="comment-item-action"
              :class="{ active: activeDiffCommentId === comment.id }"
              :disabled="loadingDiffCommentId === comment.id"
              :aria-pressed="activeDiffCommentId === comment.id"
              @click="toggleChanges(comment)"
            >{{ changesButtonLabel(comment) }}</button>
            <button v-if="canEditComment(comment)" type="button" class="comment-item-action" @click="startEdit(comment)">Edit</button>
            <button v-if="canDeleteComment(comment)" type="button" class="comment-item-action danger" @click="deleteComment(comment)">Delete</button>
          </div>
          <footer class="comment-item-time">
            {{ formatInstant(comment.createdAt) }}<template v-if="comment.editedAt"> · edited</template>
          </footer>
        </div>
      </article>
      <template v-if="document.canCreateComments">
        <textarea
          v-model="newComment"
          class="comment-item-editor"
          maxlength="4000"
          placeholder="Add a comment…"
        ></textarea>
        <div class="comment-item-actions">
          <button type="button" class="btn" :disabled="newComment.trim().length === 0" @click="createComment">Add comment</button>
        </div>
      </template>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { X } from 'lucide-vue-next';
import type { EntityId } from '../api/entityId';
import {
  CommandsService,
  QueriesService,
  type DocumentCommentResponse,
  type GetDocumentCommentsQueryResponse,
  type GetDocumentQueryResponse
} from '../api/generated';
import { formatInstant } from '../format';
import { getErrorMessage, useSessionStore } from '../stores/session';
import { useNotificationsStore } from '../stores/notifications';
import { useWorkspaceStore } from '../stores/workspace';
import UserAvatar from './UserAvatar.vue';

const props = defineProps<{
  document: GetDocumentQueryResponse;
  activeDiffCommentId: EntityId | null;
}>();

const emit = defineEmits<{
  close: [];
  showChanges: [commentId: EntityId, baselineContentMarkdown: string];
  hideChanges: [];
}>();

const session = useSessionStore();
const commentManagementWindowMs = (Number(session.profile?.commentManagementWindowSeconds ?? 300)) * 1000;
const notifications = useNotificationsStore();
const workspace = useWorkspaceStore();
const comments = ref<GetDocumentCommentsQueryResponse | null>(null);
const commentEdits = reactive<Record<EntityId, string>>({});
const editingCommentId = ref<EntityId | null>(null);
const newComment = ref('');
const message = ref<string | null>(null);
const now = ref(Date.now());
const loadingDiffCommentId = ref<EntityId | null>(null);
let nowTimer: number | null = null;
let diffRequestId = 0;
let commentsRequestId = 0;

onMounted(() => {
  void load();
  nowTimer = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  commentsRequestId++;
  diffRequestId++;
  if (nowTimer != null) {
    window.clearInterval(nowTimer);
  }
});
watch(
  [
    () => props.document.id,
    () => workspace.getDocumentCommentsRevision(String(props.document.id)),
    () => workspace.documentCommentsRecoveryRevision
  ],
  () => {
    void load();
  }
);

async function load(): Promise<void> {
  const requestId = ++commentsRequestId;
  const documentId = String(props.document.id);
  diffRequestId++;
  loadingDiffCommentId.value = null;
  message.value = null;
  comments.value = null;
  editingCommentId.value = null;
  for (const key of Object.keys(commentEdits)) {
    delete commentEdits[key];
  }

  if (!props.document.canViewComments) {
    comments.value = { comments: [] };
    return;
  }

  try {
    const response = await QueriesService.queryGetDocumentCommentsQuery({
      requestBody: { documentId }
    });
    if (requestId !== commentsRequestId || String(props.document.id) !== documentId) {
      return;
    }

    comments.value = response;
    for (const comment of comments.value.comments ?? []) {
      commentEdits[comment.id] = comment.content;
    }

    await notifications.markDocumentRead(documentId);
  } catch (error) {
    if (requestId === commentsRequestId) {
      message.value = getErrorMessage(error, 'Unable to load comments.');
    }
  }
}

async function createComment(): Promise<void> {
  try {
    const comment = await CommandsService.commandCreateDocumentCommentCommand({
      requestBody: {
        documentId: props.document.id,
        content: newComment.value
      }
    });
    comments.value ??= { comments: [] };
    upsertComment(comment);
    commentEdits[comment.id] = comment.content;
    newComment.value = '';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to create comment.');
  }
}

function startEdit(comment: DocumentCommentResponse): void {
  commentEdits[comment.id] = comment.content;
  editingCommentId.value = comment.id;
}

function cancelEdit(comment: DocumentCommentResponse): void {
  commentEdits[comment.id] = comment.content;
  editingCommentId.value = null;
}

async function saveEdit(comment: DocumentCommentResponse): Promise<void> {
  const previousContent = comment.content;
  const previousEditedAt = comment.editedAt;
  comment.content = commentEdits[comment.id] ?? comment.content;
  comment.editedAt = new Date().toISOString();
  try {
    await CommandsService.commandEditDocumentCommentCommand({
      requestBody: {
        commentId: comment.id,
        content: comment.content
      }
    });
    editingCommentId.value = null;
  } catch (error) {
    comment.content = previousContent;
    comment.editedAt = previousEditedAt;
    message.value = getErrorMessage(error, 'Unable to edit comment.');
  }
}

async function deleteComment(comment: DocumentCommentResponse): Promise<void> {
  const list = comments.value?.comments ?? [];
  const index = list.findIndex(candidate => candidate.id === comment.id);
  if (index >= 0) {
    list.splice(index, 1);
  }

  if (editingCommentId.value === comment.id) {
    editingCommentId.value = null;
  }

  if (props.activeDiffCommentId === comment.id) {
    emit('hideChanges');
  }

  try {
    await CommandsService.commandDeleteDocumentCommentCommand({ requestBody: { commentId: comment.id } });
  } catch (error) {
    if (index >= 0) {
      list.splice(index, 0, comment);
    }

    message.value = getErrorMessage(error, 'Unable to delete comment.');
  }
}

function canEditComment(comment: DocumentCommentResponse): boolean {
  return props.document.canEditComments
    && isCommentOwner(comment)
    && isWithinCommentWindow(comment);
}

function upsertComment(comment: DocumentCommentResponse): void {
  const list = comments.value?.comments;
  if (list == null) {
    return;
  }

  const index = list.findIndex(candidate => String(candidate.id) === String(comment.id));
  if (index >= 0) {
    list.splice(index, 1, comment);
    return;
  }

  list.push(comment);
}

function canShowChanges(comment: DocumentCommentResponse): boolean {
  return props.document.canViewHistory && comment.diffBaselineAvailable;
}

function changesButtonLabel(comment: DocumentCommentResponse): string {
  if (loadingDiffCommentId.value === comment.id) {
    return 'Loading changes…';
  }

  return props.activeDiffCommentId === comment.id ? 'Hide changes' : 'Show changes';
}

async function toggleChanges(comment: DocumentCommentResponse): Promise<void> {
  if (props.activeDiffCommentId === comment.id) {
    diffRequestId++;
    loadingDiffCommentId.value = null;
    emit('hideChanges');
    return;
  }

  const requestId = ++diffRequestId;
  const documentId = props.document.id;
  loadingDiffCommentId.value = comment.id;
  message.value = null;
  try {
    const response = await QueriesService.queryGetDocumentCommentDiffQuery({
      requestBody: { commentId: comment.id }
    });
    if (requestId === diffRequestId && props.document.id === documentId) {
      emit('showChanges', comment.id, response.baselineContentMarkdown);
    }
  } catch (error) {
    if (requestId === diffRequestId) {
      message.value = getErrorMessage(error, 'Unable to load changes for this comment.');
    }
  } finally {
    if (requestId === diffRequestId) {
      loadingDiffCommentId.value = null;
    }
  }
}

function canDeleteComment(comment: DocumentCommentResponse): boolean {
  if (session.isGlobalAdministrator) {
    return true;
  }

  return props.document.canDeleteComments
    && isCommentOwner(comment)
    && isWithinCommentWindow(comment);
}

function isCommentOwner(comment: DocumentCommentResponse): boolean {
  return session.profile != null && String(comment.userId) === String(session.profile.id);
}

function isWithinCommentWindow(comment: DocumentCommentResponse): boolean {
  const createdAt = new Date(String(comment.createdAt)).getTime();
  return Number.isFinite(createdAt) && now.value - createdAt <= commentManagementWindowMs;
}
</script>
